import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Pagination from '../../shared/components/Pagination';
import { KycLevel, KycStatus, KycVerification, PaginatedResponse } from '../../shared/types';
import { KycListFilters, listKyc } from './api';
import { deviceLabel } from './device';
import KycReviewModal from './KycReviewModal';

const STATUS_OPTIONS: { value: KycStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'under_review', label: 'Under review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'resubmit_required', label: 'Resubmit required' },
];

const LEVEL_OPTIONS: { value: KycLevel | ''; label: string }[] = [
  { value: '', label: 'All levels' },
  { value: 'basic', label: 'Basic' },
  { value: 'advanced', label: 'Advanced' },
];

const STATUS_BADGE: Record<KycStatus, string> = {
  pending: 'badge-warning',
  under_review: 'badge-info',
  approved: 'badge-success',
  rejected: 'badge-danger',
  resubmit_required: 'badge-warning',
};

const KycListPage: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse<KycVerification> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<KycListFilters>({ per_page: 20 });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listKyc({ ...filters, ...(page > 1 ? {} : {}) });
      setData(res);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? ((err as { response?: { data?: { error?: string; message?: string } } }).response?.data?.error
            ?? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            ?? 'Failed to load KYC submissions')
        : 'Failed to load KYC submissions';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">KYC Submissions</h1>
            <p className="text-slate-400 mt-1">Review and approve user identity verifications</p>
          </div>
        </div>

        <div className="card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={filters.status ?? ''}
              onChange={(e) => {
                setFilters((f) => ({ ...f, status: e.target.value ? (e.target.value as KycStatus) : undefined }));
                setPage(1);
              }}
              className="input"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Level</label>
            <select
              value={filters.level ?? ''}
              onChange={(e) => {
                setFilters((f) => ({ ...f, level: e.target.value ? (e.target.value as KycLevel) : undefined }));
                setPage(1);
              }}
              className="input"
            >
              {LEVEL_OPTIONS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              value={filters.user_id ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setFilters((f) => ({ ...f, user_id: v ? Number(v) : undefined }));
                setPage(1);
              }}
              className="input"
              placeholder="Filter by user id"
            />
          </div>
        </div>

        <div className="card overflow-hidden">
          {error && (
            <div className="p-4 bg-red-500/10 border-b border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Full name</th>
                      <th className="table-header">Country</th>
                      <th className="table-header">Document</th>
                      <th className="table-header">Level</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Device / IP</th>
                      <th className="table-header">Submitted</th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {data?.data.length === 0 && (
                      <tr>
                        <td className="table-cell text-center text-slate-500" colSpan={10}>
                          No submissions found.
                        </td>
                      </tr>
                    )}
                    {data?.data.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-indigo-400">#{row.id}</td>
                        <td className="table-cell text-slate-300 font-mono">#{row.user_id}</td>
                        <td className="table-cell text-slate-200">
                          {row.first_name} {row.last_name}
                        </td>
                        <td className="table-cell text-slate-400">
                          {row.country_of_residence}
                        </td>
                        <td className="table-cell text-slate-400 capitalize">
                          {row.document_type.replace('_', ' ')}
                        </td>
                        <td className="table-cell">
                          <span className="badge badge-info capitalize">{row.level}</span>
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${STATUS_BADGE[row.status]}`}>
                            {row.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="table-cell whitespace-nowrap">
                          <div className="text-slate-300 text-sm">{deviceLabel(row) ?? '—'}</div>
                          {row.ip_address && (
                            <div className="text-slate-500 text-xs font-mono">{row.ip_address}</div>
                          )}
                        </td>
                        <td className="table-cell text-slate-400 whitespace-nowrap">
                          {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : '—'}
                        </td>
                        <td className="table-cell text-right">
                          <button
                            onClick={() => setSelectedId(row.id)}
                            className="px-3 py-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-all"
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data && data.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={page}
                    lastPage={data.last_page}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <KycReviewModal
        kycId={selectedId}
        onClose={() => setSelectedId(null)}
        onUpdated={() => {
          setSelectedId(null);
          load();
        }}
      />
    </Layout>
  );
};

export default KycListPage;
